import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, useSchema } from '../../utils/seo';

export default function Taktik() {
  useSEO({
    title: 'Meine Taktik - 3 Strategien für jedes Budget | EarnProof',
    description: 'Drei bewährte Strategien für jedes Budget - von komplett kostenlos bis zum professionellen Setup. Ohne Einsatz, geringer Einsatz oder großer Einsatz.',
    keywords: 'earning strategie, geld verdienen strategie, mistplay taktik, freecash strategie, ohne einsatz geld verdienen, passive income strategie',
    canonical: 'https://example.com/taktik',
  });

  useSchema({
    type: 'HowTo',
    data: {
      name: 'Earning-Apps Strategien für jedes Budget',
      description: 'Drei bewährte Strategien zum Geldverdienen mit Apps',
      step: [
        {
          '@type': 'HowToStep',
          name: 'Ohne Einsatz Strategie',
          text: 'Starte komplett kostenlos durch clevere Kombination von Gaming-Apps. Nutze Mistplay für Google Play Guthaben und investiere dieses in Freecash und Scrambly.',
        },
        {
          '@type': 'HowToStep',
          name: 'Geringer Einsatz Strategie',
          text: 'Investiere 50-200€ für bessere Renditen. Cloud-Mining, Gaming-Apps mit Kapital und Staking kombinieren.',
        },
        {
          '@type': 'HowToStep',
          name: 'Großer Einsatz Strategie',
          text: 'Investiere 500€+ für maximale Renditen. Premium Mining-Pakete, diversifiziertes Staking und professionelle Trading-Strategien.',
        },
      ],
    },
  });

  const [activeStrategy, setActiveStrategy] = useState(0);

  const strategies = [
    {
      title: "Ohne Einsatz",
      description: "Starte komplett kostenlos durch clevere Kombination von Gaming-Apps",
      icon: "ri-gift-line",
      color: "from-green-400 to-emerald-400",
      investment: "0€",
      steps: [
        {
          step: "1. Mistplay - Die Basis",
          detail: "Spiele täglich Mobile Games auf Mistplay und sammle Punkte. Tausche diese gegen Google Play Guthaben ein. Das ist dein Startkapital ohne echtes Geld auszugeben.",
          earnings: "30-50€ Guthaben/Monat",
          time: "2-3 Std/Tag"
        },
        {
          step: "2. Freecash mit Guthaben boosten",
          detail: "Nutze dein Google Play Guthaben für In-App-Käufe in Freecash-Angeboten. Erreiche höhere Level in Spielen schneller und verdiene mehr. Bei jedem Einkauf erhältst du zusätzlich Cashback von Freecash zurück.",
          earnings: "60-100€/Monat + Cashback",
          time: "1-2 Std/Tag"
        },
        {
          step: "3. Scrambly optimieren",
          detail: "Investiere Google Play Guthaben in Power-Ups bei Scrambly. Löse mehr Wörter, erreiche höhere Levels und maximiere deine Coin-Einnahmen. Scrambly gibt dir ebenfalls Cashback auf deine In-App-Käufe.",
          earnings: "40-70€/Monat + Cashback",
          time: "1-2 Std/Tag"
        },
        {
          step: "4. Referral-System aufbauen",
          detail: "Empfehle alle drei Gaming-Apps weiter. Verdiene Provisionen für jeden geworbenen Nutzer. Teile deine Erfolge und baue passives Einkommen auf.",
          earnings: "30-80€/Monat",
          time: "30 Min/Tag"
        }
      ],
      totalEarnings: "160-300€/Monat",
      timeInvestment: "4-6 Std/Tag",
      difficulty: "Anfänger",
      pros: [
        "Kein echtes Geld nötig - 100% kostenlos",
        "Google Play Guthaben als Hebel nutzen",
        "Cashback bei In-App-Käufen in Freecash & Scrambly",
        "Einnahmen durch clevere Kombination multiplizieren",
        "Kein finanzielles Risiko",
        "Perfekt für Gaming-Fans"
      ],
      cons: [
        "Zeitintensiv am Anfang",
        "Erfordert strategisches Denken",
        "Geduld für Guthaben-Aufbau nötig"
      ]
    },
    {
      title: "Geringer Einsatz",
      description: "Investiere 50-200€ für bessere Renditen und passive Einnahmen",
      icon: "ri-seedling-line",
      color: "from-teal-400 to-cyan-400",
      investment: "50-200€",
      steps: [
        {
          step: "1. Cloud-Mining Starter-Paket",
          detail: "Investiere 50-100€ in GoMining oder ähnliche Cloud-Mining Plattformen. Erhalte tägliche passive Bitcoin-Einnahmen ohne eigene Hardware.",
          earnings: "30-60€/Monat",
          time: "10 Min/Tag"
        },
        {
          step: "2. Gaming-Apps mit Kapital beschleunigen",
          detail: "Nutze 30-50€ für In-App-Käufe in Freecash und Scrambly. Erreiche Premium-Level schneller und verdiene deutlich mehr als kostenlose Nutzer.",
          earnings: "80-120€/Monat",
          time: "1-2 Std/Tag"
        },
        {
          step: "3. Staking & Yield Farming",
          detail: "Investiere 50-100€ in Krypto-Staking auf sicheren Plattformen wie Binance oder Kraken. Verdiene 5-15% jährliche Rendite komplett passiv.",
          earnings: "20-40€/Monat",
          time: "Passiv"
        },
        {
          step: "4. Cashback maximieren",
          detail: "Nutze Cashback-Apps für alle Online-Einkäufe. Mit höherem Einkaufsvolumen steigen auch die Cashback-Einnahmen deutlich.",
          earnings: "20-40€/Monat",
          time: "Passiv"
        }
      ],
      totalEarnings: "150-260€/Monat",
      timeInvestment: "1-2 Std/Tag",
      difficulty: "Fortgeschritten",
      pros: [
        "Mehr passive Einnahmen möglich",
        "Bessere Rendite pro Zeiteinheit",
        "Kombination aus aktiv & passiv",
        "Überschaubares Risiko",
        "Schnellerer Fortschritt in Apps"
      ],
      cons: [
        "Initiales Kapital erforderlich",
        "Gewisses Verlustrisiko bei Krypto",
        "Mehr Recherche nötig",
        "Marktrisiken beachten"
      ]
    },
    {
      title: "Großer Einsatz",
      description: "Investiere 500€+ für maximale Renditen und skalierbare Systeme",
      icon: "ri-rocket-line",
      color: "from-purple-400 to-pink-400",
      investment: "500€+",
      steps: [
        {
          step: "1. Premium Mining-Pakete",
          detail: "Investiere 300-500€ in leistungsstarke Cloud-Mining Verträge. Maximale Hash-Power für höchste tägliche Erträge und langfristige passive Einnahmen.",
          earnings: "150-300€/Monat",
          time: "15 Min/Tag"
        },
        {
          step: "2. Diversifiziertes Staking-Portfolio",
          detail: "Verteile 200-300€ auf verschiedene Staking-Coins (ETH, ADA, DOT, SOL). Nutze DeFi-Protokolle für 10-25% APY und maximiere passive Einnahmen.",
          earnings: "80-150€/Monat",
          time: "Passiv"
        },
        {
          step: "3. Professionelle Trading-Strategien",
          detail: "Setze 200-400€ für automatisierte Trading-Bots und Copy-Trading von Top-Tradern ein. Nutze Leverage vorsichtig für höhere Renditen.",
          earnings: "100-250€/Monat",
          time: "1 Std/Tag"
        },
        {
          step: "4. Gaming-Apps auf Maximum",
          detail: "Investiere 100-200€ in Premium-Features aller Gaming-Apps. Erreiche Top-Tier Status und maximiere Einnahmen durch VIP-Vorteile und Boni.",
          earnings: "150-250€/Monat",
          time: "2 Std/Tag"
        },
        {
          step: "5. Referral-Marketing skalieren",
          detail: "Investiere in Marketing für deine Referral-Links (Social Media Ads, Content Creation). Baue ein passives Einkommen durch Provisionen auf.",
          earnings: "100-300€/Monat",
          time: "2 Std/Tag"
        },
        {
          step: "6. Reinvestitions-Strategie",
          detail: "Reinvestiere 50% aller Gewinne für exponentielles Wachstum. Compound-Effekt maximiert langfristige Rendite über alle Plattformen hinweg.",
          earnings: "+40-60% Wachstum",
          time: "Automatisch"
        }
      ],
      totalEarnings: "580-1250€/Monat",
      timeInvestment: "3-4 Std/Tag",
      difficulty: "Experte",
      pros: [
        "Höchste Einnahmen möglich",
        "Stark passive Komponente",
        "Skalierbar und automatisierbar",
        "Compound-Effekt nutzen",
        "Diversifizierung über alle Bereiche"
      ],
      cons: [
        "Hohes Startkapital nötig",
        "Größeres Verlustrisiko",
        "Erfahrung erforderlich",
        "Marktrisiken beachten",
        "Mehr Management-Aufwand"
      ]
    }
  ];

  const tips = [
    {
      icon: "ri-shield-check-line",
      title: "Sicherheit geht vor",
      description: "Egal welche Strategie - nutze nur verifizierte Plattformen mit guten Reviews. Investiere nie mehr als du bereit bist zu verlieren."
    },
    {
      icon: "ri-line-chart-line",
      title: "Langsam skalieren",
      description: "Starte klein und erhöhe deine Investitionen schrittweise. Sammle erst Erfahrung, bevor du größere Summen einsetzt."
    },
    {
      icon: "ri-lightbulb-line",
      title: "Google Play Guthaben als Hebel",
      description: "Bei der Ohne-Einsatz-Strategie ist Mistplay deine Basis. Das Google Play Guthaben ist wie kostenloses Startkapital für andere Apps."
    },
    {
      icon: "ri-calculator-line",
      title: "Kosten einkalkulieren",
      description: "Berechne Gebühren, Steuern und bei Mining auch Stromkosten. Nur der Nettogewinn zählt wirklich."
    },
    {
      icon: "ri-time-line",
      title: "Geduld zahlt sich aus",
      description: "Erwarte keine schnellen Reichtümer. Kontinuität und Disziplin führen zu nachhaltigem Erfolg. Die ersten Wochen sind am härtesten."
    },
    {
      icon: "ri-refresh-line",
      title: "Diversifizierung",
      description: "Setze nie alles auf eine Karte. Verteile deine Aktivitäten und Investitionen auf mehrere Plattformen und Methoden."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/85ba60419448b257fc0565dd152570bb.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gray-950/85"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Meine <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Taktik</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Drei bewährte Strategien für jedes Budget - von komplett kostenlos bis zum professionellen Setup. 
            Wähle die Taktik, die zu deiner finanziellen Situation passt.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
            <div className="flex items-center">
              <i className="ri-check-line text-teal-400 mr-2"></i>
              Für jedes Budget
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-teal-400 mr-2"></i>
              Praxiserprobt
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-teal-400 mr-2"></i>
              Schritt-für-Schritt
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Selection */}
      <section className="py-16 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Wähle deine Strategie nach Budget</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Jede Taktik ist auf ein bestimmtes Investitionsniveau zugeschnitten. 
              Starte dort, wo es für dich passt, und skaliere später.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {strategies.map((strategy, index) => (
              <div
                key={index}
                onClick={() => setActiveStrategy(index)}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                  activeStrategy === index
                    ? 'border-teal-400 bg-gray-800/50'
                    : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${strategy.color} flex items-center justify-center mb-4`}>
                  <i className={`${strategy.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{strategy.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{strategy.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Einsatz:</span>
                    <span className="text-white font-semibold">{strategy.investment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ertrag:</span>
                    <span className="text-teal-400 font-semibold">{strategy.totalEarnings}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Level:</span>
                    <span className="text-purple-400">{strategy.difficulty}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Active Strategy Details */}
          <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700">
            <div className="flex items-center mb-6">
              <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${strategies[activeStrategy].color} flex items-center justify-center mr-4`}>
                <i className={`${strategies[activeStrategy].icon} text-2xl text-white`}></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">{strategies[activeStrategy].title}</h3>
                <p className="text-gray-400">{strategies[activeStrategy].description}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-white mb-1">{strategies[activeStrategy].investment}</div>
                <div className="text-sm text-gray-400">Startkapital</div>
              </div>
              <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-teal-400 mb-1">{strategies[activeStrategy].totalEarnings}</div>
                <div className="text-sm text-gray-400">Monatlich</div>
              </div>
              <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-cyan-400 mb-1">{strategies[activeStrategy].timeInvestment}</div>
                <div className="text-sm text-gray-400">Zeitaufwand</div>
              </div>
              <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                <div className="text-2xl font-bold text-purple-400 mb-1">{strategies[activeStrategy].difficulty}</div>
                <div className="text-sm text-gray-400">Schwierigkeit</div>
              </div>
            </div>

            <div className="space-y-6 mb-8">
              <h4 className="text-xl font-bold text-white mb-4">Schritt-für-Schritt Anleitung</h4>
              {strategies[activeStrategy].steps.map((step, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-700/30 rounded-lg">
                  <div className="w-8 h-8 bg-teal-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-white mb-1">{step.step}</h5>
                    <p className="text-gray-400 text-sm mb-2">{step.detail}</p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="text-teal-400 font-semibold">💰 {step.earnings}</span>
                      <span className="text-cyan-400">⏱️ {step.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
                <h5 className="text-lg font-bold text-green-400 mb-4 flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Vorteile
                </h5>
                <ul className="space-y-2">
                  {strategies[activeStrategy].pros.map((pro, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <i className="ri-arrow-right-s-line text-green-400 mr-2 mt-0.5"></i>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-6">
                <h5 className="text-lg font-bold text-orange-400 mb-4 flex items-center">
                  <i className="ri-alert-line mr-2"></i>
                  Zu beachten
                </h5>
                <ul className="space-y-2">
                  {strategies[activeStrategy].cons.map((con, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <i className="ri-arrow-right-s-line text-orange-400 mr-2 mt-0.5"></i>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pro Tips */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Wichtige Tipps für alle Strategien</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Diese grundlegenden Prinzipien gelten unabhängig von deinem Budget. 
              Beachte sie, um Fehler zu vermeiden und nachhaltig erfolgreich zu sein.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tips.map((tip, index) => (
              <div key={index} className="p-6 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-teal-400/50 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${tip.icon} text-xl text-gray-900`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{tip.title}</h3>
                    <p className="text-gray-400 text-sm">{tip.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-teal-400/10 to-cyan-400/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Bereit durchzustarten?</h2>
          <p className="text-gray-400 mb-8">
            Wähle die Strategie, die zu deinem Budget passt, und beginne noch heute mit dem Geldverdienen. 
            Jede Reise beginnt mit dem ersten Schritt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/reviews"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-teal-400 to-cyan-400 text-gray-900 font-semibold rounded-lg hover:from-teal-300 hover:to-cyan-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-apps-line mr-2"></i>
              Apps entdecken
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-teal-400 text-teal-400 font-semibold rounded-lg hover:bg-teal-400 hover:text-gray-900 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-question-line mr-2"></i>
              Fragen stellen
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
